import { Grid, Switch } from '@mui/material';
import { RoutalDialog, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import WarningIcon from '../../../../../assets/svg/WarningIcon';
import { EventType } from '../../../../../domain/webhook/EventType';
import { Webhook, WebhookProps } from '../../../../../domain/webhook/Webhook';
import { useCreateWebhook } from '../../../../../hooks/webhooks/useCreateWebhooks';
import Button from '../../../../../react-components/Button';
import InputText from '../../../../../react-components/InputText';
import Text from '../../../../../react-components/Text';

const eventsList: EventType[] = [
  `smartmonkey.planner.2.planner.stop.reported`,
  `smartmonkey.planner.2.planner.stop.deleted`,
  `smartmonkey.planner.2.planner.plan.updated`,
  `smartmonkey.planner.2.planner.plan.deleted`,
  `smartmonkey.planner.2.planner.plan.created`,
  `smartmonkey.planner.2.drivers.route.started`,
  `smartmonkey.planner.2.drivers.route.finished`,
];

interface CreateWebhookDialogProps {
  projectId: string;
  onClose: () => void;
}

export const CreateWebhookDialog = ({ projectId, onClose }: CreateWebhookDialogProps) => {
  const { run: createWebhook } = useCreateWebhook();
  const [webhookData, setWebhookData] = useState<WebhookProps>({
    url: ``,
    enabled: true,
    enabled_events: [] as EventType[],
  } as WebhookProps);

  const isFormDisabled = () => {
    try {
      Webhook.create(webhookData);
      return false;
    } catch (e) {
      return true;
    }
  };

  return (
    <RoutalDialog
      open
      onCloseDialog={() => onClose()}
      titleContent={<Text variant="h6" intlId={`settings.developers.webhooks.create_webhook_title`} />}
      dialogProps={{
        fullWidth: true,
        maxWidth: `sm`,
      }}
      footerContent={
        <>
          <Button variant="flat" intlId="cancel" onClick={onClose} />
          <Button
            onClick={() => {
              const newWebhook = Webhook.create(webhookData);
              createWebhook({ webhook: newWebhook, projectId });
              onClose();
            }}
            disabled={isFormDisabled()}
            intlId={`settings.developers.webhooks.create_webhook_button`}
          />
        </>
      }
    >
      <Grid style={{ padding: `16px` }}>
        <VSpacer small />

        <Grid item container direction="column">
          <Grid item style={{ width: `100%` }}>
            <Text variant="label" intlId="settings.developers.webhooks.table.column.url" />
            <VSpacer small />
            <InputText
              inputContainerStyles={{ width: `100%` }}
              placeholder="https://www.example.com/test"
              validators={[
                {
                  errorMessage: (
                    <Text
                      tagName="span"
                      style={{ cursor: `default`, color: RoutalPalette.danger }}
                      intlId="settings.developers.webhooks.url_validation"
                    />
                  ),
                  isInvalid: (text?: string) => {
                    const expression1 =
                      /(https:\/\/|http:\/\/)[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
                    const regex1 = new RegExp(expression1);
                    if (text && text.match(regex1)) {
                      return false;
                    }
                    return true;
                  },
                },
              ]}
              fieldValue={``}
              onChange={(url: any) => {
                webhookData.url = url;
                setWebhookData({ ...webhookData });
              }}
            />
          </Grid>

          <VSpacer medium />

          <Grid item style={{ width: `100%` }}>
            <Text variant="label" intlId="settings.developers.webhooks.events" />

            <RoutalSelect
              id="select-events"
              style={{
                marginTop: `6px`,
                justifyContent: `space-between`,
              }}
              popoverProps={{
                options: eventsList.map(
                  (event: EventType): RoutalSelectMultiSelectOption => ({
                    key: event,
                    closeOnClick: false,
                    hideCheckIcon: false,
                    checked: (webhookData.enabled_events ?? []).includes(event),
                    onClick: () => {
                      if ((webhookData.enabled_events ?? []).includes(event)) {
                        const newEvents = [
                          ...(webhookData.enabled_events ?? []).filter((st: EventType) => st !== event),
                        ];
                        webhookData.enabled_events = newEvents;
                        setWebhookData({ ...webhookData });
                      } else {
                        webhookData.enabled_events = [...(webhookData.enabled_events ?? []), event];
                        setWebhookData({ ...webhookData });
                      }
                    },
                    content: (
                      <Text style={{ cursor: `pointer` }} intlId={`settings.developers.webhooks.events.${event}`} />
                    ),
                  })
                ),
              }}
            >
              <Text
                style={{ textAlign: `left` }}
                intlId="settings.developers.webhooks.events.number_of_selected"
                intlValues={{ num: (webhookData.enabled_events ?? []).length }}
              />
            </RoutalSelect>
          </Grid>

          <VSpacer medium />

          <Grid
            item
            container
            direction="row"
            style={{ width: `100%` }}
            alignItems="center"
            justifyContent="space-between"
            wrap="nowrap"
          >
            <Grid item>
              <Grid container direction="row" alignItems="center" wrap="nowrap">
                <Grid item>
                  <Text variant="label" intlId="settings.developers.webhooks.enabled" />
                </Grid>

                <Grid item>
                  <Switch
                    checked={webhookData.enabled ?? true}
                    onChange={(event) => {
                      const { checked } = event.target;
                      setWebhookData({ ...webhookData, enabled: checked });
                    }}
                    color="primary"
                  />
                </Grid>
              </Grid>
            </Grid>

            {webhookData.enabled !== undefined && !webhookData.enabled && (
              <Grid item>
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  style={{
                    padding: `8px`,
                    border: `1px solid ${RoutalPalette.warning}`,
                    borderRadius: `8px`,
                    backgroundColor: `rgba(248,231,28,0.1)`,
                  }}
                  wrap="nowrap"
                >
                  <Grid item>
                    <WarningIcon style={{ float: `left`, marginRight: `8px`, color: RoutalPalette.warning }} />
                  </Grid>

                  <Grid item>
                    <Text
                      intlId="settings.developers.webhooks.disabled_info"
                      style={{ float: `left`, fontSize: `11px` }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
